import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, KPITile, AIInsightCard, Section, Loading } from "../components/Common";
import { useAuth } from "../lib/auth";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell } from "recharts";
import { Sparkles, MapPin, CalendarDays, ShieldCheck, GraduationCap, BookOpenCheck, Home, Activity } from "lucide-react";
import { ROLE_LABEL } from "../lib/nav";
import { toast } from "sonner";
import { Markdown } from "../components/Common";

export default function Executive() {
  const { tenant, user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [trend, setTrend] = useState([]);
  const [insights, setInsights] = useState([]);
  const [generating, setGenerating] = useState(false);

  // Deterministic single-current-briefing selection. Executive Overview must
  // render exactly ONE current briefing (§Checkpoint 2). The backend feed can
  // contain historical briefings across period-kinds (today/week/month/…);
  // we keep the most recently generated one and discard the rest for this
  // surface. Historical records are not deleted server-side.
  const pickCurrentBriefing = (raw) => {
    if (!Array.isArray(raw) || raw.length === 0) return [];
    const kindFor = (title = "") => {
      const t = title.toLowerCase();
      if (t.includes("today") || t.includes("daily")) return "today";
      if (t.includes("week")) return "week";
      if (t.includes("month")) return "month";
      if (t.includes("quarter")) return "quarter";
      if (t.includes("annual") || t.includes("year")) return "year";
      return "briefing";
    };
    const byKind = new Map();
    for (const it of raw) {
      const k = kindFor(it.title);
      const cur = byKind.get(k);
      if (!cur || (it.generated_at || "") > (cur.generated_at || "")) byKind.set(k, it);
    }
    // Return the single most-recent current briefing across all kinds.
    const candidates = [...byKind.values()].sort((a, b) => (b.generated_at || "").localeCompare(a.generated_at || ""));
    return candidates.slice(0, 1);
  };

  useEffect(() => {
    Promise.all([
      api.execKpis().then(({ data }) => setKpis(data)),
      api.execTrend().then(({ data }) => setTrend(data.trend || [])),
      api.execInsights().then(({ data }) => setInsights(pickCurrentBriefing(data.insights || []))),
    ]).catch(() => {});
  }, []);

  const gen = async () => {
    setGenerating(true);
    try {
      const { data } = await api.execGenerate();
      // Replace (never append) the current briefing so repeated clicks do not
      // stack duplicate current cards.
      setInsights([data.insight]);
      toast.success("New AI insight generated");
    } catch {
      toast.error("Unable to generate insight");
    } finally {
      setGenerating(false);
    }
  };

  if (!kpis) return <Loading />;

  const admissions = kpis.admissions_pipeline || {};
  const admissionsBar = Object.entries(admissions).map(([stage, count]) => ({
    stage: stage.charAt(0).toUpperCase() + stage.slice(1), count,
  }));
  const behData = Object.entries(kpis.behaviour || {}).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div data-testid="executive-page">
      <ExecutiveHero tenant={tenant} user={user} latestInsight={insights[0]} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPITile testId="kpi-students" tone="dark" label="Enrolment" value={kpis.students} sub={`Current roll · ${kpis.boys ?? "—"} boys · ${kpis.girls ?? "—"} girls`} />
        <KPITile testId="kpi-boarders" label="Boarders" value={kpis.boarders} sub={`Current roll · ${kpis.day_students ?? (kpis.students - kpis.boarders)} day students`} />
        <KPITile testId="kpi-staff" label="Staff & Faculty" value={kpis.staff} sub={`Current roll · ${kpis.classes} classes`} />
        <KPITile testId="kpi-attendance" label="Attendance" value={`${kpis.attendance_pct}%`} sub={`${kpis.attendance_period_label || "Term to date"}${kpis.attendance_target_pct ? ` · target ${kpis.attendance_target_pct}%` : ""}`} accent={kpis.attendance_pct >= (kpis.attendance_target_pct || 94) ? "On target" : "Monitor closely"} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPITile testId="kpi-boys" label="Boys" value={kpis.boys ?? "—"} sub="Current roll · canonical gender split" />
        <KPITile testId="kpi-girls" label="Girls" value={kpis.girls ?? "—"} sub="Current roll · canonical gender split" />
        <KPITile testId="kpi-day" label="Day Students" value={kpis.day_students ?? (kpis.students - kpis.boarders)} sub="Current roll · not boarding" />
        <KPITile testId="kpi-collection" tone="dark" label="Fee Collection" value={`${kpis.finance.collection_pct}%`} sub={`${kpis.finance.collection_period_label || "Year to date"} · ${kpis.finance.currency_symbol || "$"}${(kpis.finance.outstanding).toLocaleString()} outstanding`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="hl-card p-5 lg:col-span-2" data-testid="attendance-trend">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline">Attendance last 14 days</div>
              <div className="font-display font-bold text-lg tracking-tight">Daily attendance %</div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="attGrad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#D9531E" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#D9531E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                <XAxis dataKey="date" fontSize={11} stroke="#94a3b8" />
                <YAxis fontSize={11} stroke="#94a3b8" domain={[80, 100]} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e2e8f0" }} />
                <Area type="monotone" dataKey="attendance_pct" stroke="#D9531E" strokeWidth={2} fill="url(#attGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="hl-card p-5" data-testid="admissions-pipeline">
          <div className="overline mb-1">Admissions pipeline</div>
          <div className="font-display font-bold text-lg tracking-tight mb-3">Live applications</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={200}>
              <BarChart data={admissionsBar}>
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                <XAxis dataKey="stage" fontSize={10} stroke="#94a3b8" />
                <YAxis fontSize={11} stroke="#94a3b8" />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="count" fill="#002147" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <Section
        title="AI Executive Intelligence"
        subtitle="GPT-powered board-ready briefings synthesised from live school data. Clearly labelled as AI-generated."
        actions={
          <button
            onClick={gen}
            disabled={generating}
            data-testid="btn-generate-insight"
            className="hl-btn-accent inline-flex items-center gap-2 disabled:opacity-60 text-sm"
          >
            <Sparkles className="w-4 h-4" />
            {generating ? "Generating…" : "Generate briefing"}
          </button>
        }
      >
        <div className="grid md:grid-cols-2 gap-4">
          {insights.length === 0 && (
            <div className="hl-card hl-ai-card p-6 md:col-span-2" data-testid="ai-briefing-empty-state">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[color:var(--hl-orange)] mb-1">Executive Intelligence · Awaiting Briefing</div>
                  <div className="font-display font-bold text-lg tracking-tight">Live Canonical Snapshot</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    <b>Live canonical data — not AI-generated narrative.</b> Tap “Generate briefing” to synthesise a board-ready summary from the current verified school dataset.
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold px-2 py-1 rounded border border-slate-200">
                  Academic Year {tenant?.academic_year || "2026"} · {tenant?.current_term || "Term"}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm" data-testid="canonical-snapshot">
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Enrolment · Current roll</div><div className="font-display font-bold text-xl text-[color:var(--hl-navy)]">{kpis.students}</div><div className="text-[10px] text-slate-500">{kpis.boys} boys · {kpis.girls} girls</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Boarding · Current roll</div><div className="font-display font-bold text-xl text-[color:var(--hl-navy)]">{kpis.boarders}</div><div className="text-[10px] text-slate-500">{kpis.day_students ?? (kpis.students - kpis.boarders)} day scholars</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Attendance · {kpis.attendance_period_label || "Term to date"}</div><div className="font-display font-bold text-xl text-[color:var(--hl-navy)]">{kpis.attendance_pct}%</div><div className="text-[10px] text-slate-500">canonical roll</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Fee Collection · {kpis.finance.collection_period_label || "Year to date"}</div><div className="font-display font-bold text-xl text-[color:var(--hl-navy)]">{kpis.finance.collection_pct}%</div><div className="text-[10px] text-slate-500">{kpis.finance.currency_symbol || "$"}{(kpis.finance.outstanding).toLocaleString()} outstanding</div></div>
              </div>
              <div className="mt-4 text-[11px] text-slate-500 italic">
                No previously generated briefing is currently in the intelligence cache. AI narrative is generated on demand — no synthetic content is fabricated at first load.
              </div>
            </div>
          )}
          {insights.map((ins) => (
            <AIInsightCard
              key={ins.id}
              testId={`ai-insight-${ins.id}`}
              title={ins.title}
              body={ins.body}
              severity={ins.severity}
            />
          ))}
        </div>
      </Section>

      <Section title="Behaviour signals" subtitle="Positive commendations vs disciplinary incidents (30-day rolling)">
        <div className="hl-card p-5 h-56" data-testid="behaviour-chart">
          <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={200}>
            <BarChart data={behData} layout="vertical">
              <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
              <XAxis type="number" fontSize={11} stroke="#94a3b8" />
              <YAxis dataKey="name" type="category" fontSize={11} stroke="#94a3b8" width={100} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {behData.map((d, i) => (
                  <Cell key={i} fill={d.name === "positive" ? "#059669" : d.name === "major" ? "#dc2626" : "#D97706"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>
    </div>
  );
}

/**
 * Executive Workspace hero — pure UI refinement.
 * Visual hierarchy (largest → smallest):
 *   L1  Institution (uppercase)
 *   L2  Executive Workspace
 *   L3  Harare, Zimbabwe · Academic Year 2026 · <current_term from tenant> · Principal
 *   L4  Greeting (contextual)
 *
 * Consumes the existing Executive Intelligence service — no new endpoints,
 * no new business logic. Only the header block was changed.
 */
function ExecutiveHero({ tenant, user, latestInsight }) {
  const hour = new Date().getHours();
  const daypart = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
  const surname = (user?.full_name || "").split(" ").slice(-1)[0] || "Head";
  const roleLabel = ROLE_LABEL[user?.role] || "Executive";
  const location = [tenant?.city, tenant?.country].filter(Boolean).join(", ") || "Harare, Zimbabwe";
  const schoolName = (tenant?.name || "").toUpperCase();
  const established = tenant?.established_year || 1998;

  const badges = [
    { icon: MapPin, label: tenant?.country || "Zimbabwe" },
    { icon: BookOpenCheck, label: "Cambridge & ZIMSEC" },
    { icon: Home, label: "Boarding & Day School" },
    { icon: GraduationCap, label: `Established ${established}` },
    { icon: Sparkles, label: "Executive Intelligence Active" },
    { icon: Activity, label: "Operational" },
  ];

  return (
    <section
      className="relative overflow-hidden rounded-2xl mb-6 shadow-[0_20px_60px_-30px_rgba(0,33,71,0.45)] border border-slate-200/70"
      data-testid="executive-hero"
      style={{
        background:
          "linear-gradient(135deg, #001432 0%, #002147 45%, #04244d 100%)",
      }}
    >
      {/* Subtle orange accent stripe */}
      <div
        className="absolute inset-x-0 top-0 h-[3px]"
        style={{ background: "linear-gradient(90deg, transparent 0%, #D9531E 30%, #D9531E 70%, transparent 100%)" }}
        aria-hidden
      />
      {/* Ambient depth */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.14]" aria-hidden style={{
        backgroundImage:
          "radial-gradient(600px 240px at 12% 0%, rgba(217,83,30,0.65), transparent 60%), radial-gradient(500px 220px at 90% 100%, rgba(59,130,246,0.35), transparent 60%)",
      }} />

      <div className="relative px-6 sm:px-10 pt-9 sm:pt-11 pb-8 sm:pb-10">
        {/* L2 — Kicker */}
        <div
          className="text-[10px] sm:text-[11px] uppercase tracking-[0.42em] font-semibold text-[color:var(--hl-orange)] mb-3"
          data-testid="hero-kicker"
        >
          Executive Workspace
        </div>

        {/* L1 — Institution (dominant) */}
        <h1
          className="font-display font-black text-white tracking-[-0.01em] leading-[0.98] break-words"
          style={{ fontSize: "clamp(2rem, 5.4vw, 4rem)" }}
          data-testid="hero-institution"
        >
          {schoolName || "HIGHLANDS INTERNATIONAL COLLEGE"}
        </h1>

        {/* L3 — Contextual meta */}
        <div
          className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-white/75 text-[13px] sm:text-sm"
          data-testid="hero-context"
        >
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-[color:var(--hl-orange)]" /> {location}
          </span>
          <span className="hidden sm:inline text-white/25">·</span>
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays className="w-3.5 h-3.5 text-[color:var(--hl-orange)]" /> Academic Year {tenant?.academic_year || "2026"}
          </span>
          <span className="hidden sm:inline text-white/25">·</span>
          <span>{tenant?.current_term || "Term"}</span>
          <span className="hidden sm:inline text-white/25">·</span>
          <span className="inline-flex items-center gap-1.5 uppercase tracking-widest text-[10px] font-semibold text-white/85">
            <ShieldCheck className="w-3 h-3 text-[color:var(--hl-orange)]" /> {roleLabel}
          </span>
        </div>

        {/* L4 — Greeting (contextual, subdued) */}
        <div
          className="mt-3 text-white/55 text-xs sm:text-[13px] italic"
          data-testid="hero-greeting"
        >
          Good {daypart}, {user?.full_name || `Dr ${surname}`}. Welcome back to the Executive Workspace.
        </div>

        {/* Identity strip — presentation only */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="hero-identity-strip">
          {badges.map(({ icon: Icon, label }, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold text-white/85 border border-white/15 bg-white/[0.06] backdrop-blur-sm"
            >
              <Icon className="w-3 h-3 text-[color:var(--hl-orange)]" /> {label}
            </span>
          ))}
        </div>

        {/* Executive Summary — consumes existing Executive Intelligence */}
        {latestInsight?.body && (
          <div
            className="mt-6 rounded-xl border border-white/10 bg-white/[0.05] backdrop-blur-md p-4 sm:p-5"
            data-testid="hero-executive-summary"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold text-[color:var(--hl-orange)] mb-1.5">
              <Sparkles className="w-3 h-3" /> Executive Summary
              <span className="ml-auto text-white/40 normal-case tracking-normal font-normal">
                {latestInsight.generated_at ? new Date(latestInsight.generated_at).toLocaleString() : ""}
              </span>
            </div>
            <div className="text-white/80 text-[13px] leading-relaxed line-clamp-6 [&_p]:text-white/80 [&_strong]:text-white [&_h4]:text-white/90 [&_h5]:text-white/90 [&_li]:text-white/80 [&_.markdown-lite]:space-y-1.5">
              <Markdown>{latestInsight.body.length > 720 ? `${latestInsight.body.slice(0, 720)}…` : latestInsight.body}</Markdown>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
